<!-- src/components/SliderCaptcha.vue -->
<template>
  <!-- 滑块验证码容器 -->
  <div class="slider-captcha" :class="{ 'is-success': verified }">
    <!-- 滑块轨道背景 -->
    <div
      ref="sliderTrack"
      class="slider-track"
      :style="trackStyle"
      @mousedown="startDrag"
      @touchstart.prevent="startDrag"
    >
      <!-- 进度填充条，随拖动实时变化 -->
      <div class="slider-fill" :style="fillStyle"></div>
      <!-- 提示文字，拖动时隐藏 -->
      <div class="slider-text" :class="{ 'is-dragging': isDragging }">
        {{ verified ? successText : placeholderText }}
      </div>
      <!-- 可拖动滑块按钮 -->
      <div
        ref="sliderBtn"
        class="slider-btn"
        :style="btnStyle"
        @mousedown="startDrag"
        @touchstart.prevent="startDrag"
      >
        <!-- 成功时显示对勾图标，否则显示箭头图标 -->
        <Icon v-if="verified" type="md-checkmark" />
        <Icon v-else type="md-arrow-forward" />
      </div>
    </div>
    <!-- 验证结果提示信息 -->
    <div v-if="message" class="slider-message" :class="messageType">
      {{ message }}
    </div>
  </div>
</template>

<script>
// 导入 View UI Plus 图标组件
import { Icon } from 'view-ui-plus';
// 导入 API 客户端，用于请求后端验证接口
import apiClient from '@/api/index';
// 导入加密库，用于加密轨迹数据
import CryptoJS from 'crypto-js';

export default {
  // 组件名称
  name: 'SliderCaptcha',

  // 注册子组件
  components: {
    Icon
  },

  // 组件接收的属性配置
  props: {
    // 轨道宽度（像素）
    width: {
      type: Number,
      default: 300
    },
    // 未验证时的提示文字
    placeholderText: {
      type: String,
      default: '按住滑块拖动到最右侧完成验证'
    },
    // 验证成功后的提示文字
    successText: {
      type: String,
      default: '验证成功'
    }
  },

  // 组件内部数据
  data() {
    return {
      isDragging: false,      // 是否正在拖动
      startX: 0,              // 拖动开始时的鼠标/触摸X坐标
      startY: 0,              // 拖动开始时的鼠标/触摸Y坐标
      currentX: 0,            // 滑块当前位置（像素）
      trackWidth: 0,          // 轨道实际宽度（像素）
      btnWidth: 40,           // 滑块按钮宽度（像素）
      verified: false,        // 是否验证通过
      sliderToken: '',        // 后端返回的验证令牌
      captchaKey: '',         // 滑块验证码唯一标识
      message: '',            // 提示信息文本
      messageType: '',        // 提示信息类型：success / error
      // ===== 轨迹采集相关 =====
      trajectory: [],         // 轨迹数据数组 [{x, y, t}, ...]
      dragStartTime: 0,       // 拖动开始时间戳
      lastPointTime: 0,       // 上一个轨迹点的时间戳
      pauseCount: 0,          // 拖动过程中的停顿次数
      mouseDownCount: 0,      // 鼠标按下次数（防脚本）
      // ===== AES 加密密钥 =====
      aesKey: '',             // 后端返回的 AES 密钥
      aesIv: ''               // 后端返回的 AES IV
    };
  },

  // 计算属性
  computed: {
    // 轨道动态样式
    trackStyle() {
      return {
        width: `${this.width}px`
      };
    },
    // 进度填充条动态样式
    fillStyle() {
      return {
        width: `${this.currentX}px`
      };
    },
    // 滑块按钮动态样式
    btnStyle() {
      return {
        transform: `translateX(${this.currentX}px)`,
        left: '0px'
      };
    },
    // 滑块可移动的最大距离
    maxMove() {
      return this.trackWidth - this.btnWidth;
    }
  },

  // 组件挂载后初始化
  mounted() {
    // 获取轨道实际宽度
    this.trackWidth = this.$refs.sliderTrack ? this.$refs.sliderTrack.offsetWidth : this.width;
    // 从后端获取滑块验证码配置
    this.refreshCaptcha();
    // 绑定全局鼠标/触摸事件，用于拖动过程中和结束时的处理
    this.bindEvents();
  },

  // 组件销毁前清理事件监听
  beforeDestroy() {
    this.unbindEvents();
  },

  // 组件方法
  methods: {
    // 绑定全局鼠标/触摸事件
    bindEvents() {
      document.addEventListener('mousemove', this.onDragging);
      document.addEventListener('mouseup', this.stopDrag);
      document.addEventListener('touchmove', this.onDragging, { passive: false });
      document.addEventListener('touchend', this.stopDrag);
    },

    // 移除全局鼠标/触摸事件
    unbindEvents() {
      document.removeEventListener('mousemove', this.onDragging);
      document.removeEventListener('mouseup', this.stopDrag);
      document.removeEventListener('touchmove', this.onDragging);
      document.removeEventListener('touchend', this.stopDrag);
    },

    // 从后端获取新的滑块验证码（含AES密钥）
    async refreshCaptcha() {
      try {
        const response = await apiClient.get('/captcha/slider/generate');
        if (response.data.code === 200) {
          this.captchaKey = response.data.data.captcha_key;
          this.aesKey = response.data.data.aes_key || '';
          this.aesIv = response.data.data.aes_iv || '';
        }
      } catch (error) {
        console.error('获取滑块验证码失败:', error);
      }
    },

    // 开始拖动
    startDrag(event) {
      // 已验证则不可再拖动
      if (this.verified) return;
      this.isDragging = true;
      this.message = '';
      // 获取鼠标或触摸的起始坐标
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      this.startX = clientX - this.currentX;
      this.startY = clientY;
      // 初始化轨迹采集
      this.dragStartTime = Date.now();
      this.lastPointTime = Date.now();
      this.trajectory = [{ x: 0, y: Math.round(clientY), t: 0 }];
      this.pauseCount = 0;
      this.mouseDownCount++;
    },

    // 拖动中 - 采集轨迹点
    onDragging(event) {
      if (!this.isDragging) return;
      // 阻止默认行为，防止页面滚动
      if (event.type === 'touchmove') {
        event.preventDefault();
      }
      // 计算当前鼠标/触摸位置
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      let moveX = clientX - this.startX;
      // 限制滑块在轨道范围内
      if (moveX < 0) moveX = 0;
      if (moveX > this.maxMove) moveX = this.maxMove;

      // 采集轨迹数据
      const now = Date.now();
      const timeDelta = now - this.lastPointTime;
      // 停顿检测：超过 200ms 未移动计为一次停顿
      if (timeDelta > 200 && this.currentX > 0 && moveX !== this.currentX) {
        this.pauseCount++;
      }
      this.lastPointTime = now;

      this.trajectory.push({
        x: Math.round(moveX),
        y: Math.round(clientY),
        t: now - this.dragStartTime
      });

      this.currentX = moveX;
    },

    // 停止拖动
    stopDrag() {
      if (!this.isDragging) return;
      this.isDragging = false;
      // 只有当拖动到轨道末端附近（>= 90%）时才提交验证
      if (this.currentX >= this.maxMove * 0.9) {
        this.verifySlider();
      } else {
        // 未拖到位则复位
        this.resetSlider();
      }
    },

    // 生成随机干扰数据，用于混淆请求内容
    generateNoise() {
      const noise = [];
      const noiseCount = Math.floor(Math.random() * 8) + 3;
      for (let i = 0; i < noiseCount; i++) {
        noise.push({
          r: Math.random().toString(36).substring(2, 10),
          v: Math.floor(Math.random() * 100000),
          ts: Date.now() + Math.floor(Math.random() * 10000)
        });
      }
      return noise;
    },

    // 构建并AES加密行为数据
    buildEncryptedPayload() {
      const totalTime = Date.now() - this.dragStartTime;
      const ratio = this.maxMove > 0 ? this.currentX / this.maxMove : 0;

      // 构建行为数据载荷（含轨迹和干扰数据）
      const payload = {
        // 核心验证数据
        ratio: parseFloat(ratio.toFixed(4)),
        total_time: totalTime,
        // 轨迹数据
        trajectory: this.trajectory,
        // 行为特征
        pause_count: this.pauseCount,
        point_count: this.trajectory.length,
        mouse_down_count: this.mouseDownCount,
        // 环境数据
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        // 干扰噪点数据
        noise: this.generateNoise()
      };

      const jsonStr = JSON.stringify(payload);

      // 使用 AES-128-CBC 加密
      const encrypted = CryptoJS.AES.encrypt(
        jsonStr,
        CryptoJS.enc.Utf8.parse(this.aesKey),
        {
          iv: CryptoJS.enc.Utf8.parse(this.aesIv),
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7
        }
      );
      // 只取密文部分（不含Salted头），转为Base64
      return encrypted.ciphertext.toString(CryptoJS.enc.Base64);
    },

    // 向后端提交加密的行为数据进行验证
    async verifySlider() {
      try {
        const ciphertext = this.buildEncryptedPayload();
        const response = await apiClient.post('/captcha/slider/verify', {
          captcha_key: this.captchaKey,
          payload: ciphertext
        });
        if (response.data.code === 200) {
          // 验证成功
          this.verified = true;
          this.sliderToken = response.data.data.slider_token;
          this.currentX = this.maxMove;
          this.message = '验证成功';
          this.messageType = 'success';
          // 通知父组件验证成功，并传递令牌
          this.$emit('success', this.sliderToken);
        } else {
          // 验证失败
          this.resetSlider();
          this.message = response.data.msg || '验证失败，请重试';
          this.messageType = 'error';
          this.$emit('fail');
        }
      } catch (error) {
        this.resetSlider();
        this.message = '网络错误，请重试';
        this.messageType = 'error';
        this.$emit('fail');
        console.error('滑块验证失败:', error);
      }
    },

    // 重置滑块状态（清空轨迹等）
    resetSlider() {
      this.currentX = 0;
      this.verified = false;
      this.sliderToken = '';
      this.trajectory = [];
      this.pauseCount = 0;
      this.mouseDownCount = 0;
      this.refreshCaptcha();
    }
  }
};
</script>

<style scoped>
/* 滑块验证码外层容器 */
.slider-captcha {
  user-select: none;
  -webkit-user-select: none;
}

/* 滑块轨道 */
.slider-track {
  position: relative;
  height: 40px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  cursor: default;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* 进度填充条 */
.slider-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, #2d8cf0, #64b5f6);
  border-radius: 4px 0 0 4px;
  transition: width 0.1s;
}

/* 提示文字 */
.slider-text {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #666;
  pointer-events: none;
  transition: opacity 0.2s;
}

/* 拖动时隐藏提示文字 */
.slider-text.is-dragging {
  opacity: 0.3;
}

/* 验证成功后文字变白 */
.slider-captcha.is-success .slider-text {
  color: #fff;
}

/* 滑块按钮 */
.slider-btn {
  position: absolute;
  top: 0;
  width: 40px;
  height: 40px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #666;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: background 0.2s, color 0.2s;
}

/* 鼠标悬停时按钮样式 */
.slider-btn:hover {
  color: #2d8cf0;
  border-color: #2d8cf0;
}

/* 验证成功后按钮样式 */
.slider-captcha.is-success .slider-btn {
  background: #52c41a;
  color: #fff;
  border-color: #52c41a;
  cursor: default;
}

/* 提示信息 */
.slider-message {
  margin-top: 6px;
  font-size: 12px;
  text-align: center;
}

.slider-message.success {
  color: #52c41a;
}

.slider-message.error {
  color: #ff4d4f;
}
</style>
